import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Badge, Box, Typography } from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { grey } from "@mui/material/colors";
import { AnimatePresence, motion } from "framer-motion";
import { CartSummaryChip } from "../../../styled/pos/windows11";
import { setShowCartDrawer } from "../../../../store/actions/newOrderSlice";
import { formatFinance } from "../../../../utils/formatters";

/**
 * CompactCartSummary - Compact cart display with animations
 *
 * Empty state: Grey styling, "Carrinho vazio"
 * Filled state: Blue background, "R$ X,XX • N itens"
 *
 * Animations:
 * - Badge pulse on item count change (spring animation)
 * - Chip pulse when items added
 *
 * Click: Opens cart drawer
 */
const CompactCartSummary = ({ containerRef, iconOnly }) => {
  const dispatch = useDispatch();
  const cartProducts = useSelector((state) => state.newOrder.cartProducts);
  const total = useSelector((state) => state.newOrder.total);

  const [prevItemCount, setPrevItemCount] = useState(0);
  const [shouldPulse, setShouldPulse] = useState(false);

  const itemCount = cartProducts.length;
  const isFilled = itemCount > 0;

  const handleClick = () => {
    dispatch(setShowCartDrawer(true));
  };

  // Trigger pulse animation when item count increases
  useEffect(() => {
    if (itemCount > prevItemCount && itemCount > 0) {
      setShouldPulse(true);
      const timer = setTimeout(() => setShouldPulse(false), 600);
      return () => clearTimeout(timer);
    }
    setPrevItemCount(itemCount);
  }, [itemCount, prevItemCount]);

  // Spring config for animations
  const springConfig = {
    type: "spring",
    stiffness: 500,
    damping: 30,
  };

  // Empty state
  if (!isFilled) {
    return (
      <CartSummaryChip
        ref={containerRef}
        filled={false}
        onClick={handleClick}
        aria-label="Abrir carrinho vazio"
      >
        <ShoppingCartIcon sx={{ fontSize: "1.25rem", color: grey[600] }} />
        {!iconOnly && (
          <Typography variant="body2" sx={{ fontWeight: 500, color: grey[700] }}>
            Carrinho vazio
          </Typography>
        )}
      </CartSummaryChip>
    );
  }

  // Filled state with animations
  return (
    <motion.div
      ref={containerRef}
      animate={shouldPulse ? { scale: [1, 1.1, 1] } : {}}
      transition={{ duration: 0.6, ease: "easeInOut" }}
      style={{ display: "inline-block" }}
    >
      <CartSummaryChip
        filled={true}
        itemcount={itemCount}
        onClick={handleClick}
        aria-label={`Abrir carrinho com ${itemCount} ${
          itemCount === 1 ? "item" : "itens"
        }`}
      >
        {/* Animated Badge */}
        <Badge
          badgeContent={
            <AnimatePresence mode="wait">
              <motion.div
                key={itemCount}
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={springConfig}
              >
                {itemCount}
              </motion.div>
            </AnimatePresence>
          }
          color="error"
          sx={{
            "& .MuiBadge-badge": {
              fontWeight: 700,
              fontSize: "0.75rem",
            },
          }}
        >
          <ShoppingCartIcon sx={{ fontSize: "1.25rem" }} />
        </Badge>

        {/* Cart summary text */}
        {!iconOnly && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography
              variant="body2"
              component="span"
              sx={{ fontWeight: 600, color: "#fff" }}
            >
              {formatFinance(total)}
            </Typography>
            <Typography
              variant="body2"
              component="span"
              sx={{ opacity: 0.9, color: "#fff" }}
            >
              •
            </Typography>
            <Typography
              variant="body2"
              component="span"
              sx={{ opacity: 0.9, color: "#fff" }}
            >
              {itemCount} {itemCount === 1 ? "item" : "itens"}
            </Typography>
          </Box>
        )}
      </CartSummaryChip>
    </motion.div>
  );
};

export default CompactCartSummary;
